import React, { useState, useEffect, useRef } from 'react';
import { WhopCheckoutEmbed } from '@whop/checkout/react';
import api from '../../api/client';
import CopyButton from './CopyButton';
import ResultIcon from './ResultIcon';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 120000;

/**
 * Beágyazott Whop checkout. A user az oldalon marad — a kártyamezők a Whop
 * iframe-jében élnek (PCI), a rendelést a metadata.order_id köti össze a
 * payment.succeeded webhookkal.
 */
export default function CardPaymentModal({ order, sessionId, planLabel, onClose }) {
  const [paid, setPaid] = useState(false);
  const [licenseKey, setLicenseKey] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const pollStartedAt = useRef(null);

  // Fizetés után a webhook kézbesíti a kulcsot — addig pollozzuk a rendelést
  useEffect(() => {
    if (!paid || licenseKey) return;
    pollStartedAt.current = Date.now();

    const interval = setInterval(async () => {
      if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        clearInterval(interval);
        return;
      }
      try {
        const { data } = await api.get(`/user/orders/${order.id}`);
        if (data.order.status === 'delivered' && data.order.license_key) {
          setLicenseKey(data.order.license_key);
          clearInterval(interval);
        }
      } catch {}
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [paid, licenseKey, order.id]);

  return (
    // A görgetés a külső rétegen van, a középre igazítás pedig egy min-h-full
    // wrapperen belül. Enélkül (flex items-center + overflow a SAJÁT elemen) a
    // magas tartalom felfelé is kilóg, és a teteje — a bezáró gomb — elérhetetlen.
    // A Whop iframe pont ilyenkor nő meg, amikor a vásárló kinyitja a kuponmezőt.
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-black/70 backdrop-blur-md animate-fade-in">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-2xl bg-gray-900/95 backdrop-blur-2xl
                        border border-white/[0.09] shadow-modal animate-scale-in">
          <span className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

          <div className="flex items-start gap-3 p-5 pb-4">
            <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-500/15 border border-brand-400/25
                             text-brand-200 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-base font-semibold text-white">Pay with Card</h2>
              <p className="text-sm text-gray-500 mt-0.5">{planLabel} — €{order.final_price}</p>
            </div>
            <button onClick={onClose} className="btn-icon -mt-1 -mr-1 flex-shrink-0" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" className="w-4 h-4">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-5 pb-5">
            {licenseKey ? (
              <div className="text-center pt-2">
                <ResultIcon tone="success" className="mb-5" />
                <p className="font-display text-white font-semibold mb-1">Payment confirmed</p>
                <p className="text-gray-500 text-sm mb-6">
                  Your license key is ready. Activate it in your dashboard.
                </p>

                <div className="surface-sunken px-3.5 py-2.5 mb-6 text-left">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    License key
                  </p>
                  <div className="flex items-center justify-between gap-2">
                    <code className="font-mono text-gray-100 text-sm tracking-wider break-all">
                      {licenseKey}
                    </code>
                    <CopyButton value={licenseKey} variant="icon" />
                  </div>
                </div>

                <button onClick={onClose} className="btn-primary w-full py-2.5">Close</button>
              </div>
            ) : paid ? (
              <div className="text-center py-8">
                <div className="flex items-center justify-center gap-2.5 mb-3">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-400" />
                  </span>
                  <p className="text-white font-medium">Payment received</p>
                </div>
                {timedOut ? (
                  <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                    Delivery is taking longer than usual. Your key will appear in your
                    dashboard shortly — contact support if it does not.
                  </p>
                ) : (
                  <p className="text-gray-500 text-sm">Delivering your license key…</p>
                )}
              </div>
            ) : (
              <>
                <WhopCheckoutEmbed
                  sessionId={sessionId}
                  theme="dark"
                  themeOptions={{
                    // brand-500 — ugyanaz a világosabb lila, amit a .btn-primary
                    // gradiensének a teteje használ
                    accentColor: '#8f52c9',
                    // A modal lapjának ténylegesen kirajzolt színe: a
                    // bg-gray-900/95 a bg-black/70 overlay + #0a0810 oldalháttér felett.
                    backgroundColor: '#100d17',
                    borderRadius: 10,            // rounded-lg
                  }}
                  returnUrl={`${window.location.origin}/panel`}
                  onComplete={() => setPaid(true)}
                  fallback={
                    <div className="py-12 text-center text-sm text-gray-500">
                      Loading secure checkout…
                    </div>
                  }
                />
                <p className="text-center text-xs text-gray-600 mt-4 leading-relaxed">
                  Payments are processed securely by Whop. We never see or store your card details.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
